import { useCallback, useMemo } from "react";
import type { Editor } from "@tiptap/react";
import { parseTemplate } from "@/parser/templateParser";
import { serializeTemplate } from "@/parser/templateSerializer";
import type { TemplateMeta } from "@/parser/templateSerializer";
import { contentToNodes } from "@/components/unified/templateToDocument";
import { documentToContentNodes } from "@/components/unified/documentToContentNodes";
import type { Template, ContentNode } from "@/types/template";

function serializeFromEditor(editor: Editor, meta: TemplateMeta): string {
  return serializeTemplate(documentToContentNodes(editor.getJSON()), meta);
}

function remapContentNodes(nodes: ContentNode[], prefix: string): ContentNode[] {
  return nodes.map((node) => {
    if (node.type === "field") {
      return { ...node, field: { ...node.field, id: `${prefix}_${node.field.id}` } };
    }
    if (node.type === "section") {
      return { ...node, id: `${prefix}_${node.id}` };
    }
    return node;
  });
}

interface SaveAsParams {
  cloneFromSeed: (seedId: string, editedSource: string) => Promise<Template>;
  editorRef: React.RefObject<Editor | null>;
  meta: TemplateMeta;
  navigate: (path: string) => void | Promise<void>;
  template: Template | undefined;
  toast: (msg: string, type?: "success" | "error" | "info") => void;
}

export function useSaveAsHandler(params: SaveAsParams) {
  const { cloneFromSeed, editorRef, meta, navigate, template, toast } = params;

  return useCallback(async () => {
    const editor = editorRef.current;
    if (!editor || !template) {
      return;
    }
    const source = serializeFromEditor(editor, meta);
    const clone = await cloneFromSeed(template.id, source);
    toast("Saved as your custom copy", "success");
    void navigate(`/build/${clone.id}`);
  }, [cloneFromSeed, editorRef, meta, navigate, template, toast]);
}

interface InsertParams {
  allTemplates: Template[];
  editorRef: React.RefObject<Editor | null>;
  pendingInsertPosRef: React.RefObject<number | null>;
  setShowInsertPicker: React.Dispatch<React.SetStateAction<boolean>>;
  toast: (msg: string, type?: "success" | "error" | "info") => void;
}

export function useInsertHandlers(params: InsertParams) {
  const { allTemplates, editorRef, pendingInsertPosRef, setShowInsertPicker, toast } = params;

  const handleInsertTemplate = (templateId: string) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const tmpl = allTemplates.find((t) => t.id === templateId);
    if (!tmpl) {
      return;
    }
    const parsed = parseTemplate(tmpl.source);
    const remapped = remapContentNodes(parsed.content.content, `ins${Date.now()}`);
    const pos = pendingInsertPosRef.current ?? editor.state.selection.to;
    pendingInsertPosRef.current = null;
    editor.chain().insertContentAt(pos, contentToNodes(remapped)).focus().run();
    setShowInsertPicker(false);
    toast(`"${parsed.content.name}" inserted`, "success");
  };

  const pickerTemplates = useMemo(
    () =>
      allTemplates.map((t) => {
        const p = parseTemplate(t.source);
        return {
          category: p.content.category,
          id: t.id,
          name: p.content.name,
          sectionCount: p.content.content.filter((n) => n.type === "section").length,
        };
      }),
    [allTemplates],
  );

  return { handleInsertTemplate, pickerTemplates };
}
